import React from 'react';
import { useTranslation } from 'react-i18next';
import { User, Hash, MapPin, List, Film } from "lucide-react";

/**
 * Renders the list of saved audience rules.
 *
 * @param {Array}   savedAudienceRules                 - Array of rules to display.
 * @param {number}  openDropdown                      - Index of the currently open dropdown, or null.
 * @param {Function} toggleDropdown                   - Handler to toggle dropdown menus.
 * @param {Function} removeSavedAudienceRule          - Handler to remove a specific rule.
 * @param {Function} changeStatusSavedAudienceRule    - Handler to toggle a rule's status between Active/Disabled.
 * @param {Function} modifySavedAudienceRule          - Handler to modify (edit) a specific rule.
 * @param {Object}  dropdownRef                       - Reference used to close the dropdown if user clicks outside.
 */
export interface RuleCriteria {
  type: string;
  filter?: string;
  id?: string;
  caption?: string;
  thumbnail?: string;
}

export interface SavedRule {
  _id?: string;
  rule_name: string;
  rule: RuleCriteria[];
  status: string;
  created_at?: string;
}

interface Props {
  savedAudienceRules: SavedRule[];
  openDropdown: number | null;
  toggleDropdown: (index: number) => void;
  removeSavedAudienceRule: (index: number) => void;
  changeStatusSavedAudienceRule: (index: number) => void;
  modifySavedAudienceRule: (index: number) => void;
  dropdownRef: React.MutableRefObject<HTMLDivElement | null>;
  expandedRules: Set<number>;
  toggleExpandRule: (index: number) => void;
}

// Helper function to get icon for criteria type
const getIconForCriteria = (type: string): React.ReactElement => {
  if (type.toLowerCase().includes('user')) {
    return <User size={16} className="text-blue-600" />;
  } else if (type.toLowerCase().includes('hashtag')) {
    return <Hash size={16} className="text-purple-600" />;
  } else if (type.toLowerCase().includes('place')) {
    return <MapPin size={16} className="text-red-500" />;
  } else if (type.toLowerCase().includes('posts')) {
    return <List size={16} className="text-yellow-600" />;
  } else if (type.toLowerCase().includes('reels')) {
    return <Film size={16} className="text-pink-600" />;
  }
  return <List size={16} className="text-gray-600" />;
};

// Helper function to get translated criteria type name
const getTranslatedCriteriaType = (type: string, t: (key: string) => string): string => {
  return t(`setMyAudience.filterCriteria.names.${type}`);
};

const AudienceMyRules: React.FC<Props> = ({
  savedAudienceRules,
  openDropdown,
  toggleDropdown,
  removeSavedAudienceRule,
  changeStatusSavedAudienceRule,
  modifySavedAudienceRule,
  dropdownRef,
  expandedRules,
  toggleExpandRule,
}) => {
  const { t } = useTranslation();
  // Calculate statistics
  const activeRules = savedAudienceRules.filter(rule => rule.status === 'Active').length;
  const totalRules = savedAudienceRules.length;
  const totalCriteria = savedAudienceRules.reduce((sum, rule) => sum + (rule.rule?.length || 0), 0);

  return (
    <div className="famous-clicker-audience-module-container">
      {/* Section Header */}
      <div className="mb-4 pb-3 border-b border-gray-200" style={{ flexShrink: 0 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
              <i className="bi bi-list-check text-white text-sm"></i>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">{t('audienceMyRules.titles.main')}</h2>
              <p className="text-xs text-gray-600">{t('audienceMyRules.titles.subtitle')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Card */}
      {savedAudienceRules.length > 0 && (
        <div className="mb-4 p-3 bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-700">{t('audienceMyRules.titles.overview')}</span>
            <i className="bi bi-bar-chart text-blue-600"></i>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="text-center p-2 bg-white rounded border border-gray-100">
              <div className="text-lg font-bold text-blue-600">{totalRules}</div>
              <div className="text-xs text-gray-600">{t('audienceMyRules.labels.total')}</div>
            </div>
            <div className="text-center p-2 bg-white rounded border border-gray-100">
              <div className="text-lg font-bold text-green-600">{activeRules}</div>
              <div className="text-xs text-gray-600">{t('audienceMyRules.labels.active')}</div>
            </div>
            <div className="text-center p-2 bg-white rounded border border-gray-100">
              <div className="text-lg font-bold text-purple-600">{totalCriteria}</div>
              <div className="text-xs text-gray-600">{t('audienceMyRules.labels.criteria')}</div>
            </div>
          </div>
        </div>
      )}

      {savedAudienceRules.length === 0 ? (
        <div className="famous-clicker-audience-module-no-rules">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="bi bi-folder-x text-gray-400 text-2xl"></i>
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">{t('audienceMyRules.messages.noRules')}</p>
            <p className="text-xs text-gray-500">{t('audienceMyRules.messages.createFirst')}</p>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
        {savedAudienceRules.map((rule, index: number) => {
          const isExpanded = expandedRules.has(index);
          return (
          <div
            key={rule._id} // Use `_id` instead of `index` for better React key usage
            className="famous-clicker-audience-module"
            ref={openDropdown === index ? dropdownRef : null}
            style={{ cursor: 'pointer' }}
          >
            <div className={`famous-clicker-audience-module-icon famous-clicker-audience-${rule.rule_name}`}>
              {rule.rule_name.toLowerCase() === 'draft' ? (
                <i className="fas fa-pencil-alt"></i>
              ) : (
                <i className="fas fa-book-open"></i>
              )}
            </div>

            <div
              className="famous-clicker-audience-module-content"
              onClick={() => toggleExpandRule(index)}
              style={{ flex: 1 }}
            >
              <div className="flex items-center justify-between">
                <div className="famous-clicker-audience-module-title">{rule.rule_name}</div>
                <button
                  className="text-gray-500 hover:text-gray-700 transition-transform duration-200"
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    padding: '2px 6px'
                  }}
                >
                  <i className="bi bi-chevron-down"></i>
                </button>
              </div>
              <div className="famous-clicker-audience-module-subtitle">
                {t('audienceMyRules.labels.criteriaCount', { count: rule.rule.length })} • {t('audienceMyRules.labels.created')} {new Date(rule.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </div>

              {!isExpanded ? (
                // Preview of criteria types when collapsed
                <div className="flex flex-wrap gap-1 mt-2">
                  {Array.from(new Set<string>(rule.rule.map((r) => r.type))).slice(0, 2).map((type, idx) => (
                    <span key={idx} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                      {getTranslatedCriteriaType(type, t)}
                    </span>
                  ))}
                  {Array.from(new Set<string>(rule.rule.map((r) => r.type))).length > 2 && (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                      +{Array.from(new Set<string>(rule.rule.map((r) => r.type))).length - 2} {t('audienceMyRules.labels.more')}
                    </span>
                  )}
                </div>
              ) : (
                // Full list of all criteria when expanded
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-2">
                  {rule.rule.map((criteria, criteriaIdx: number) => (
                    <div
                      key={criteriaIdx}
                      className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getIconForCriteria(criteria.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-700 truncate">
                          {criteria.filter}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {getTranslatedCriteriaType(criteria.type, t)}
                        </div>
                      </div>
                      {criteria.thumbnail && (
                        <img
                          src={criteria.thumbnail}
                          alt="Criteria thumbnail"
                          className="w-10 h-10 rounded object-cover flex-shrink-0"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="famous-clicker-audience-module-stats">
              <span className={`status-${rule.status?.toLowerCase()}`}>
                {rule.status}
              </span>
            </div>

            <button
              className="famous-clicker-audience-menu-button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                toggleDropdown(index);
              }}
              aria-label="Rule options"
              aria-expanded={openDropdown === index}
            >
              ⋮
            </button>

            <div
              className={`famous-clicker-audience-dropdown-menu ${
                openDropdown === index ? 'famous-clicker-audience-show' : ''
              }`}
              role="menu"
              aria-label="Rule actions"
            >
              <button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  removeSavedAudienceRule(index);
                }}
                role="menuitem"
              >
                {t('audienceMyRules.buttons.delete')}
              </button>
              <button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  changeStatusSavedAudienceRule(index);
                }}
                role="menuitem"
              >
                {rule.status === 'Active' ? t('audienceMyRules.buttons.disable') : t('audienceMyRules.buttons.enable')}
              </button>
              <button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  modifySavedAudienceRule(index);
                }}
                role="menuitem"
              >
                {t('audienceMyRules.buttons.modify')}
              </button>
            </div>
          </div>
        );
        })}
        </div>
      )}
    </div>
  );
};

export default AudienceMyRules;